/**
 * The reporting window an optimizer run analyses.
 *
 * Until now nothing sent one, so each platform fell back to its own default —
 * and those defaults disagreed. Google Ads and Reddit read **year to date**,
 * LinkedIn read the last 30 days, Facebook the last 30, and DV360 / TTD /
 * MadHive / YouTube DV360 read whatever their reporting source hands over. So
 * "optimize this campaign" meant a different question per platform, and on the
 * Google family it meant "look at everything since January".
 *
 * The default is now **last 30 days**, everywhere it can be expressed.
 *
 * ── What each platform can actually be told ─────────────────────────────────
 * This is the part that cannot be papered over, because the optimizer's Python
 * handlers genuinely differ (campaign-services/handlers/campaign_optimizer_handler.py):
 *
 *   Google Search / PMax / DemandGen
 *       `start_date` + `end_date`, which WIN over a preset
 *       (_resolve_date_range: `if start_date and end_date: return ...`).
 *       → an arbitrary custom range is honoured exactly.
 *
 *   Reddit, LinkedIn
 *       `date_preset` only — one of a fixed vocabulary.
 *       → presets are honoured; an arbitrary range cannot be expressed, so it
 *         is not offered.
 *
 *   Facebook
 *       `days_back` (an integer).
 *       → a preset maps cleanly; a custom range maps to its length in days.
 *
 *   DV360, TTD, MadHive, YouTube DV360
 *       nothing. These read an uploaded report or pull their own window, and
 *       there is no parameter to pass. → NO control is rendered and NO window
 *       fields are sent. Not a disabled picker, and not a stated default either:
 *       naming a default would be a claim about behaviour nothing implements.
 *
 * Callers should use `optimizeDateRangeSupport(destination)` to decide what to
 * render, and `buildOptimizeDateParams()` to turn a choice into request fields.
 */

import { canonicalPlatform } from './accountSettingPlatformList';

/** The preset vocabulary every preset-taking handler understands. */
export const OPTIMIZE_DATE_PRESETS = [
  { value: 'last_7_days', label: 'Last 7 days', days: 7 },
  { value: 'last_30_days', label: 'Last 30 days', days: 30 },
  { value: 'last_90_days', label: 'Last 90 days', days: 90 },
  { value: 'ytd', label: 'Year to date', days: null },
  { value: 'all_time', label: 'All time', days: null },
];

/** What the optimizer has always defaulted to from here on. */
export const DEFAULT_OPTIMIZE_PRESET = 'last_30_days';

/**
 * How much of a date range this platform's optimizer can be told.
 *
 * @returns {'range' | 'preset' | 'none'}
 */
export function optimizeDateRangeSupport(destination) {
  const p = canonicalPlatform(destination);
  if (p === 'google-ads-search' || p === 'google-ads-pmax' || p === 'google-ads-demandgen') {
    return 'range';
  }
  // Facebook takes days_back, which every preset maps onto, but not an
  // arbitrary pair of dates — so it belongs with the preset-only platforms.
  if (p === 'reddit' || p === 'linkedin' || p === 'facebook') return 'preset';
  return 'none';
}

/** Does this platform's optimizer read a reporting window at all? */
export function optimizeAcceptsDateWindow(destination) {
  return optimizeDateRangeSupport(destination) !== 'none';
}

/**
 * The latest date a custom range may end on: yesterday, in the user's own
 * calendar. Today has no complete reporting; a range that reaches it is moved
 * back a day by the backend anyway, so the picker does not offer it.
 */
export function latestOptimizeDate(today = new Date()) {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Concrete { startDate, endDate } for a preset, or null for the open-ended ones
 * (ytd / all_time), which are better expressed as the preset itself. "Last N
 * days" is exactly N complete days, ending yesterday.
 */
export function presetToRange(preset, today = new Date()) {
  const entry = OPTIMIZE_DATE_PRESETS.find((p) => p.value === preset);
  if (!entry?.days) return null;
  const endDate = latestOptimizeDate(today);
  const [y, m, d] = endDate.split('-').map(Number);
  const start = new Date(y, m - 1, d - (entry.days - 1));
  const startDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
  return { startDate, endDate };
}

/** Whole days spanned by an explicit range, or null when it isn't a valid one. */
export function rangeToDays(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const a = Date.parse(startDate);
  const b = Date.parse(endDate);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  return Math.max(1, Math.round((b - a) / 86400000));
}

/**
 * Turn a user's choice into the fields `POST /campaigns/:id/optimize` takes.
 *
 * @param {object} choice { preset, startDate, endDate } — `preset: 'custom'`
 *   means use the explicit dates.
 * @param {string} destination the campaign's platform
 * @returns {object} request fields; `{}` when the platform reads no window, so
 *   the call is byte-identical to one made before this existed.
 */
export function buildOptimizeDateParams(choice, destination) {
  const support = optimizeDateRangeSupport(destination);
  if (support === 'none') return {};

  const preset = choice?.preset || DEFAULT_OPTIMIZE_PRESET;

  if (preset === 'custom') {
    // Only the Google family can be told an arbitrary range. Anywhere else the
    // control is not offered, so reaching here means a caller passed one anyway
    // — fall back to the default preset rather than sending dates that would be
    // silently ignored.
    if (support !== 'range') return { datePreset: DEFAULT_OPTIMIZE_PRESET };
    const days = rangeToDays(choice?.startDate, choice?.endDate);
    if (!days) return { datePreset: DEFAULT_OPTIMIZE_PRESET };
    return { startDate: choice.startDate, endDate: choice.endDate, daysBack: days };
  }

  const params = { datePreset: preset };
  // Explicit dates alongside the preset, where they are understood: they win in
  // the Python resolver, which removes any doubt about which window ran.
  if (support === 'range') {
    const range = presetToRange(preset);
    if (range) Object.assign(params, range);
  }
  const entry = OPTIMIZE_DATE_PRESETS.find((p) => p.value === preset);
  if (entry?.days) params.daysBack = entry.days;
  return params;
}

/** Human summary of a choice, for confirmation copy. */
export function describeOptimizeWindow(choice, destination) {
  if (optimizeDateRangeSupport(destination) === 'none') return 'all available data';
  const preset = choice?.preset || DEFAULT_OPTIMIZE_PRESET;
  if (preset === 'custom') {
    const days = rangeToDays(choice?.startDate, choice?.endDate);
    return days ? `${choice.startDate} → ${choice.endDate}` : 'last 30 days';
  }
  return (OPTIMIZE_DATE_PRESETS.find((p) => p.value === preset)?.label || 'Last 30 days').toLowerCase();
}

export default {
  OPTIMIZE_DATE_PRESETS,
  DEFAULT_OPTIMIZE_PRESET,
  optimizeDateRangeSupport,
  optimizeAcceptsDateWindow,
  presetToRange,
  latestOptimizeDate,
  rangeToDays,
  buildOptimizeDateParams,
  describeOptimizeWindow,
};
